
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChecklistWithTaches } from '@/hooks/useNouvellesChecklists';
import { Sparkles, Ship, Anchor, Snowflake, List, Edit, RotateCcw, Trash2, ChevronDown } from 'lucide-react';

interface ListeChecklistsNouvellesProps {
  checklists: ChecklistWithTaches[];
  onModifierTache: (tacheId: string, etat: boolean) => void;
  onReinitialiser: (checklistId: string) => void;
  onSupprimer: (checklistId: string) => void;
  onModifierChecklist: (checklist: ChecklistWithTaches) => void;
}

const ListeChecklistsNouvelles = ({ 
  checklists, 
  onModifierTache, 
  onReinitialiser, 
  onSupprimer,
  onModifierChecklist
}: ListeChecklistsNouvellesProps) => {
  const [openChecklists, setOpenChecklists] = useState<Set<string>>(new Set());

  const toggleChecklist = (checklistId: string) => {
    const newOpenChecklists = new Set(openChecklists);
    if (newOpenChecklists.has(checklistId)) {
      newOpenChecklists.delete(checklistId);
    } else {
      newOpenChecklists.add(checklistId);
    }
    setOpenChecklists(newOpenChecklists);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Nettoyage':
        return <Sparkles className="h-5 w-5 text-purple-600" />;
      case 'Départ':
        return <Ship className="h-5 w-5 text-blue-600" />;
      case 'Arrivée':
        return <Anchor className="h-5 w-5 text-green-600" />;
      case 'Hivernage':
        return <Snowflake className="h-5 w-5 text-cyan-600" />;
      default:
        return <List className="h-5 w-5 text-gray-600" />;
    }
  };

  if (checklists.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mes Checklists</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4 text-4xl">📋</div>
            <p className="text-muted-foreground">Aucune checklist disponible pour ce navire</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {checklists.map((checklist) => (
        <Card key={checklist.id}>
          <Collapsible 
            open={openChecklists.has(checklist.id)} 
            onOpenChange={() => toggleChecklist(checklist.id)}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-4 cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getTypeIcon(checklist.type)}
                    <CardTitle className="text-lg">{checklist.nom}</CardTitle>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                        openChecklists.has(checklist.id) ? 'rotate-180' : ''
                      }`} 
                    />
                  </div>
                  <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onModifierChecklist(checklist)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Modifier la checklist"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onReinitialiser(checklist.id)}
                      className="text-nautic hover:text-nautic-light hover:bg-nautic/5"
                      title="Réinitialiser toutes les tâches"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onSupprimer(checklist.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Supprimer la checklist"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-3">
                  {checklist.taches
                    .sort((a, b) => a.ordre - b.ordre)
                    .map((tache, index) => (
                       <div key={tache.id} className="flex items-center space-x-3" onClick={(e) => e.stopPropagation()}>
                         <Checkbox
                           id={`tache-${tache.id}`}
                           checked={tache.etat}
                           onCheckedChange={(checked) => onModifierTache(tache.id, checked as boolean)}
                           className="border-nautic data-[state=checked]:bg-nautic-gold data-[state=checked]:border-nautic-gold"
                         />
                         <label
                           htmlFor={`tache-${tache.id}`}
                           className={`flex-1 text-sm cursor-pointer ${
                             tache.etat ? 'line-through text-muted-foreground' : ''
                           }`}
                         >
                           {tache.tache}
                         </label>
                       </div>
                    ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      ))}
    </div>
  );
};

export default ListeChecklistsNouvelles;
